import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Chip, CircularProgress, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  MoreVert,
  PlayArrow as RunIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import {
  deleteSourceConfig,
  fetchSourceConfigs,
  runSourceConfig,
} from "@accredo/fe-shared/services/importSourceConfigService.js";
import ImportSourceConfigFormModal from "../ImportSourceConfigFormModal.jsx";

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function ImportSourceConfigsTable() {
  const { enqueueSnackbar } = useSnackbar();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [running, setRunning] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [rowMenuState, setRowMenuState] = useState({ anchorEl: null, menuRowId: null });

  const load = useCallback(() => {
    setLoading(true);
    fetchSourceConfigs()
      .then((res) => {
        setConfigs(res.data ?? []);
        setHasLoaded(true);
      })
      .catch(() =>
        enqueueSnackbar("Import-Quellen konnten nicht geladen werden", {
          variant: "error",
        })
      )
      .finally(() => setLoading(false));
  }, [enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  function openCreate() {
    setEditingId(null);
    setModalOpen(true);
  }
  function openEdit(row) {
    setEditingId(row.id);
    setModalOpen(true);
  }
  function handleModalClose(saved) {
    setModalOpen(false);
    if (saved) load();
  }

  function handleMenuOpen(e, id) {
    e.stopPropagation();
    setRowMenuState({ anchorEl: e.currentTarget, menuRowId: id });
  }
  function handleMenuClose(e) {
    e?.stopPropagation();
    setRowMenuState({ anchorEl: null, menuRowId: null });
  }

  async function handleDelete(e, config) {
    handleMenuClose(e);
    if (!window.confirm(`Import-Quelle „${config.name}" wirklich löschen?`)) return;
    try {
      await deleteSourceConfig(config.id);
      enqueueSnackbar("Import-Quelle gelöscht", { variant: "success" });
      load();
    } catch {
      enqueueSnackbar("Löschen fehlgeschlagen", { variant: "error" });
    }
  }

  const handleRun = useCallback(
    async (config) => {
      setRunning((prev) => ({ ...prev, [config.id]: true }));
      try {
        await runSourceConfig(config.id);
        enqueueSnackbar(`„${config.name}" wurde gestartet`, {
          variant: "success",
        });
        load();
      } catch {
        enqueueSnackbar(`Fehler beim Starten von „${config.name}"`, {
          variant: "error",
        });
      } finally {
        setRunning((prev) => ({ ...prev, [config.id]: false }));
      }
    },
    [enqueueSnackbar, load]
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        Cell: ({ row }) => (
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {row.original.name}
            </Typography>
            {row.original.description && (
              <Typography variant="caption" color="text.secondary">
                {row.original.description}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        id: "connector_name",
        header: "Connector",
        accessorFn: (row) =>
          row.connector?.name ?? String(row.import_connector_id),
      },
      {
        accessorKey: "schedule_cron",
        header: "Zeitplan",
        Cell: ({ cell }) =>
          cell.getValue() ? (
            <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
              {cell.getValue()}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Manuell
            </Typography>
          ),
      },
      {
        accessorKey: "last_run_at",
        header: "Letzter Lauf",
        Cell: ({ cell }) => formatDate(cell.getValue()),
      },
      {
        accessorKey: "is_active",
        header: "Aktiv",
        size: 90,
        Cell: ({ cell }) => (
          <Chip
            size="small"
            label={cell.getValue() ? "Aktiv" : "Inaktiv"}
            color={cell.getValue() ? "success" : "default"}
          />
        ),
      },
      {
        id: "actions",
        header: "",
        size: 56,
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnHiding: false,
        Cell: ({ row }) => (
          <>
            <IconButton size="small" onClick={(e) => handleMenuOpen(e, row.original.id)}>
              <MoreVert fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={rowMenuState.anchorEl}
              open={Boolean(rowMenuState.anchorEl) && rowMenuState.menuRowId === row.original.id}
              onClose={handleMenuClose}
            >
              <MenuItem
                onClick={(e) => { handleMenuClose(e); handleRun(row.original); }}
                disabled={!row.original.is_active || running[row.original.id]}
              >
                {running[row.original.id]
                  ? <CircularProgress size={16} sx={{ mr: 1 }} />
                  : <RunIcon fontSize="small" sx={{ mr: 1 }} />}
                Jetzt ausführen
              </MenuItem>
              <MenuItem onClick={(e) => handleDelete(e, row.original)}>
                <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                Löschen
              </MenuItem>
            </Menu>
          </>
        ),
      },
    ],
    [running, handleRun, rowMenuState]
  );

  return (
    <>
      <DataTable
        columns={columns}
        data={configs}
        isLoading={loading}
        hasLoaded={hasLoaded}
        manualSorting={false}
        manualFiltering={false}
        enableGlobalFilter={true}
        enableColumnFilters={false}
        onRowClick={openEdit}
        toolbarActions={[
          {
            type: "button",
            label: "Neu",
            icon: <AddIcon />,
            onClick: openCreate,
            variant: "primary",
          },
        ]}
      />

      <ImportSourceConfigFormModal
        open={modalOpen}
        onClose={handleModalClose}
        sourceConfigId={editingId}
      />
    </>
  );
}
